import React, { useState } from "react";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { IoTrash, IoAdd } from "react-icons/io5";
import { apiRequest } from "services/tenantUserAdminService";
import { TC } from "../tokens/text";

const VAT_BOXES = [
  { value: "box1", label: "Box 1 — Output VAT" },
  { value: "box2", label: "Box 2 — EU Acquisitions VAT" },
  { value: "box4", label: "Box 4 — Input VAT (reclaimed)" },
  { value: "box6", label: "Box 6 — Sales net" },
  { value: "box7", label: "Box 7 — Purchases net" },
  { value: "box8", label: "Box 8 — EU Supplies" },
  { value: "box9", label: "Box 9 — EU Acquisitions" },
];

const inputSx = {
  "& .MuiInputBase-input": { color: "#fff" },
  "& .MuiInputLabel-root": { color: TC.secondary },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#7a94b0" },
  "& .MuiSelect-icon": { color: TC.secondary },
};

/*Bearer ${token}`
 * VAT Adjustment Form
 * Displays existing adjustments and allows adding/removing them.
 *
 * Props:
 *   clientId    — Firestore clientProfile ID
 *   tenantId    — tenant ID
 *   periodId    — YYYY-MM period
 *   adjustments — current adjustments array from draft
 *   locked      — boolean: true when draft is submitted (no edits)
 *   onUpdated   — callback(updatedDraft) after successful add/remove
 */
function VATAdjustmentForm({ clientId, tenantId, periodId, adjustments = [], locked = false, onUpdated }) {
  const [box, setBox]       = useState("box1");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [error, setError]   = useState("");

  const basePath = `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/vat-drafts/${encodeURIComponent(periodId)}/adjustments`;

  const handleAdd = async () => {
    if (!box || !amount || !reason.trim()) {
      setError("Please fill in box, amount and reason.");
      return;
    }
    setSaving(true); setError("");
    try {
      const updated = await apiRequest(basePath, {
        method: "PATCH",
        body: { adjustment: { box, amount: Number(amount), reason: reason.trim() } },
      });
      setAmount(""); setReason(""); setBox("box1");
      if (onUpdated) onUpdated(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (adjId) => {
    setRemoving(adjId); setError("");
    try {
      const updated = await apiRequest(`${basePath}/${encodeURIComponent(adjId)}`, { method: "DELETE" });
      if (onUpdated) onUpdated(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setRemoving(null);
    }
  };

  const getAdjustmentBoxName = (value) => VAT_BOXES.find((b) => b.value === value)?.label || value;

  return (
    <VuiBox>
      <VuiTypography sx={{ color: TC.midtone, fontSize: 13, fontWeight: 600, mb: 1.5 }}>
        Adjustments
      </VuiTypography>

      {adjustments.length === 0 && (
        <VuiTypography sx={{ color: TC.secondary, fontSize: 12, mb: 2 }}>
          No adjustments. Add one below to override computed box values.
        </VuiTypography>
      )}

      {/* Existing adjustments list */}
      {adjustments.map((adj) => {
        const adjustmentBoxName = getAdjustmentBoxName(adj.box);
        return (
          <VuiBox key={adj.id}
            display="flex" alignItems="center" justifyContent="space-between"
            sx={{ background: "#f4f8ff", borderRadius: 2, p: "8px 12px", mb: 1 }}>
            <VuiBox>
              <VuiTypography sx={{ color: TC.primary, fontSize: 12, fontWeight: 700 }}>
                {adjustmentBoxName}
                &nbsp;
                <span style={{ color: adj.amount >= 0 ? "#01B574" : "#f44336" }}>
                  {adj.amount >= 0 ? "+" : ""}{adj.amount?.toLocaleString("en-GB", { style: "currency", currency: "GBP" })}
                </span>
              </VuiTypography>
              <VuiTypography sx={{ color: TC.secondary, fontSize: 11, mt: 0.25 }}>
                {adj.reason} · {adj.addedBy}
              </VuiTypography>
            </VuiBox>
            {!locked && (
              <IconButton
                size="small"
                disabled={removing === adj.id}
                onClick={() => handleRemove(adj.id)}
                aria-label={`Remove adjustment for ${adjustmentBoxName}`}
                sx={{ color: "#f44336", opacity: removing === adj.id ? 0.4 : 1 }}>
                <IoTrash size={14} aria-hidden="true" />
              </IconButton>
            )}
          </VuiBox>
        );
      })}

      {/* Add new adjustment */}
      {!locked && (
        <>
          <Divider sx={{ borderColor: "#7a94b0", my: 2 }} />
          <VuiTypography sx={{ color: TC.secondary, fontSize: 12, mb: 1.5 }}>Add Adjustment</VuiTypography>

          {error && (
            <VuiBox mb={1.5} sx={{ background: "rgba(255,71,87,0.1)", borderRadius: 1.5, p: "8px 12px" }} role="alert">
              <VuiTypography sx={{ color: "#f44336", fontSize: 12 }}>{error}</VuiTypography>
            </VuiBox>
          )}

          <FormControl fullWidth size="small" sx={{ ...inputSx, mb: 1.5 }}>
            <InputLabel sx={{ color: TC.secondary, fontSize: 12 }}>Box</InputLabel>
            <Select value={box} onChange={e => setBox(e.target.value)} label="Box"
              sx={{ color: TC.primary, fontSize: 12, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#7a94b0" } }}>
              {VAT_BOXES.map(b => <MenuItem key={b.value} value={b.value} sx={{ fontSize: 12 }}>{b.label}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            label="Amount (+ to add, - to deduct)"
            type="number"
            size="small"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            fullWidth
            sx={{ ...inputSx, mb: 1.5, "& .MuiInputBase-input": { color: TC.primary, fontSize: 12 } }}
            inputProps={{ step: "0.01" }}
          />

          <TextField
            label="Reason"
            size="small"
            value={reason}
            onChange={e => setReason(e.target.value)}
            fullWidth
            sx={{ ...inputSx, mb: 2, "& .MuiInputBase-input": { color: TC.primary, fontSize: 12 } }}
          />

          <VuiButton
            size="small"
            color="info"
            variant="outlined"
            startIcon={<IoAdd aria-hidden="true" />}
            onClick={handleAdd}
            disabled={saving}
            sx={{ fontSize: 12 }}
            aria-label="Add VAT adjustment"
          >
            {saving ? "Adding…" : "Add Adjustment"}
          </VuiButton>
        </>
      )}
    </VuiBox>
  );
}

export default VATAdjustmentForm;


